import { useEffect, useRef, useState } from "react";
import { MapContainer, TileLayer, Polygon, Marker, useMapEvents, useMap } from "react-leaflet";
import L from "leaflet";
import { Search, Loader2, Layers, MapPin } from "lucide-react";
import { useTranslation } from "react-i18next";
import { apiClient } from "../lib/api";

// Fix leaflet default icon paths
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

// Available satellite tile layers, ordered by freshness for Spain.
const TILE_LAYERS = {
  pnoa: {
    label: "PNOA (España, oficial)",
    url: "https://www.ign.es/wmts/pnoa-ma?layer=OI.OrthoimageCoverage&style=default&tilematrixset=GoogleMapsCompatible&Service=WMTS&Request=GetTile&Version=1.0.0&Format=image/jpeg&TileMatrix={z}&TileCol={x}&TileRow={y}",
    attribution: "PNOA · IGN España",
    maxZoom: 20,
  },
  google: {
    label: "Google Satélite",
    url: "https://mt1.google.com/vt/lyrs=s&x={x}&y={y}&z={z}",
    attribution: "© Google",
    maxZoom: 21,
  },
  esri: {
    label: "Esri World Imagery",
    url: "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}",
    attribution: "Tiles © Esri",
    maxZoom: 20,
  },
};

function ClickCollector({ onAdd }) {
  useMapEvents({
    click(e) { onAdd([e.latlng.lat, e.latlng.lng]); },
  });
  return null;
}

function Recenter({ center, zoom }) {
  const map = useMap();
  useEffect(() => {
    if (center && Array.isArray(center)) {
      map.flyTo(center, zoom ?? map.getZoom(), { duration: 0.8 });
    }
  }, [center?.[0], center?.[1]]);
  return null;
}

export default function RoofMap({ initial = [], onChange, initialCenter, initialAddress = "" }) {
  const { t } = useTranslation();
  const [points, setPoints] = useState(initial);
  const [center, setCenter] = useState(initialCenter || [40.4168, -3.7038]);
  const [zoom, setZoom] = useState(initialCenter ? 19 : 6);
  const [query, setQuery] = useState(initialAddress);
  const [searching, setSearching] = useState(false);
  const [matchInfo, setMatchInfo] = useState(initialCenter ? t("portal.roof.centered_from_bill") : "");
  const [layerKey, setLayerKey] = useState("pnoa");
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggest, setShowSuggest] = useState(false);
  const [highlight, setHighlight] = useState(-1);
  const debounceRef = useRef(null);
  const layer = TILE_LAYERS[layerKey];

  useEffect(() => {
    if (initialCenter && Array.isArray(initialCenter)) {
      setCenter(initialCenter);
      setZoom(19);
      setMatchInfo(t("portal.roof.centered_from_bill"));
    }
  }, [initialCenter?.[0], initialCenter?.[1]]);

  // Debounced autocomplete: fire /geocode/suggest 350ms after typing stops.
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!query || query.trim().length < 3) {
      setSuggestions([]); setShowSuggest(false); return;
    }
    // Skip suggesting if the query exactly equals the already-applied match
    if (matchInfo && matchInfo.includes(query)) {
      setSuggestions([]); setShowSuggest(false); return;
    }
    debounceRef.current = setTimeout(async () => {
      try {
        const r = await apiClient.get("/geocode/suggest", { params: { q: query } });
        setSuggestions(r.data?.results || []);
        setShowSuggest(true);
        setHighlight(-1);
      } catch {
        setSuggestions([]); setShowSuggest(false);
      }
    }, 350);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [query]);

  const addPoint = (latlng) => {
    const next = [...points, latlng];
    setPoints(next);
    onChange?.(next);
  };

  const reset = () => {
    setPoints([]);
    onChange?.([]);
  };

  const pick = (item) => {
    setCenter([item.lat, item.lng]);
    setZoom(19);
    setMatchInfo(`📍 ${item.display_name}`);
    setQuery(item.display_name);
    setSuggestions([]);
    setShowSuggest(false);
  };

  const search = async () => {
    if (!query.trim()) return;
    // If we already have suggestions, pick the first one (matches user expectation).
    if (suggestions.length > 0) {
      pick(suggestions[0]);
      return;
    }
    setSearching(true);
    setMatchInfo("");
    try {
      const r = await apiClient.get("/geocode", { params: { q: query } });
      setCenter([r.data.lat, r.data.lng]);
      setZoom(19);
      setMatchInfo(`📍 ${r.data.display_name}`);
    } catch (e) {
      setMatchInfo(t("portal.roof.address_not_found"));
    } finally {
      setSearching(false);
    }
  };

  const onKey = (e) => {
    if (!showSuggest || suggestions.length === 0) {
      if (e.key === "Enter") search();
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlight((h) => Math.min(suggestions.length - 1, h + 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlight((h) => Math.max(0, h - 1));
    } else if (e.key === "Enter") {
      e.preventDefault();
      const idx = highlight >= 0 ? highlight : 0;
      pick(suggestions[idx]);
    } else if (e.key === "Escape") {
      setShowSuggest(false);
    }
  };

  return (
    <div className="space-y-3" data-testid="roof-map">
      <div className="flex flex-col sm:flex-row gap-2 relative">
        <div className="flex-1 relative">
          <div className="flex items-center bg-[#FFF7ED] rounded-xl px-3">
            <Search className="w-4 h-4 text-[#7D927E] mr-2 shrink-0" />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={onKey}
              onFocus={() => suggestions.length > 0 && setShowSuggest(true)}
              onBlur={() => setTimeout(() => setShowSuggest(false), 200)}
              placeholder={t("portal.roof.address_search_placeholder")}
              data-testid="map-address-input"
              autoComplete="off"
              className="bg-transparent w-full py-2.5 outline-none text-sm text-[#0F172A] placeholder:text-[#94A3B8]"
            />
          </div>
          {showSuggest && suggestions.length > 0 && (
            <div data-testid="map-address-suggestions"
              className="absolute z-[1000] left-0 right-0 top-full mt-1 bg-white border border-[#E5E9EE] rounded-xl shadow-xl max-h-72 overflow-y-auto">
              {suggestions.map((s, i) => (
                <button
                  key={`${s.lat}-${s.lng}-${i}`}
                  type="button"
                  data-testid={`map-suggest-${i}`}
                  onMouseDown={(e) => { e.preventDefault(); pick(s); }}
                  onMouseEnter={() => setHighlight(i)}
                  className={`w-full text-left px-4 py-2.5 text-sm flex items-start gap-2 transition ${
                    i === highlight ? "bg-[#FFF7ED]" : "hover:bg-[#F7F8FA]"
                  }`}>
                  <MapPin className="w-4 h-4 text-[#E78938] shrink-0 mt-0.5" />
                  <span className="text-[#0F172A] leading-tight">{s.display_name}</span>
                </button>
              ))}
            </div>
          )}
        </div>
        <button
          onClick={search}
          disabled={searching || !query.trim()}
          data-testid="map-address-search"
          className="bg-[#0F172A] hover:bg-[#1E293B] text-white rounded-xl px-5 py-2.5 text-sm font-medium disabled:opacity-50 transition-all flex items-center gap-2 justify-center"
        >
          {searching ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
          {t("portal.roof.search")}
        </button>
      </div>
      {matchInfo && (
        <div className="text-xs text-[#5C5A56]" data-testid="map-match-info">{matchInfo}</div>
      )}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 text-sm">
        <span className="text-[#5C5A56]">
          {t("portal.roof.draw_hint")}
        </span>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-1.5 bg-[#FFF7ED] rounded-lg px-2 py-1.5">
            <Layers className="w-3.5 h-3.5 text-[#7D927E]" />
            <select
              value={layerKey}
              onChange={(e) => setLayerKey(e.target.value)}
              data-testid="map-layer-select"
              className="bg-transparent text-xs text-[#0F172A] outline-none cursor-pointer"
            >
              {Object.entries(TILE_LAYERS).map(([k, v]) => (
                <option key={k} value={k}>{v.label}</option>
              ))}
            </select>
          </div>
          <button onClick={reset} data-testid="map-reset"
            className="text-[#C55928] hover:underline">{t("portal.roof.reset")}</button>
        </div>
      </div>
      <MapContainer center={center} zoom={zoom} maxZoom={layer.maxZoom}>
        <TileLayer
          key={layerKey}
          attribution={layer.attribution}
          url={layer.url}
          maxZoom={layer.maxZoom}
        />
        <Recenter center={center} zoom={zoom} />
        <ClickCollector onAdd={addPoint} />
        {points.map((p, i) => <Marker key={i} position={p} />)}
        {points.length >= 3 && (
          <Polygon positions={points} pathOptions={{ color: "#C55928", weight: 3, fillOpacity: 0.25 }} />
        )}
      </MapContainer>
      <div className="flex justify-between items-center text-xs text-[#7D927E]">
        <span className="mono" data-testid="map-points-count">{t("portal.roof.points_count", { n: points.length })}</span>
        <span>{t("portal.roof.imagery_hint")}</span>
      </div>
    </div>
  );
}
